/**
 * Iter 126 — Smart Daily Huddle page.
 *
 * Two tabs:
 *   - Today: my open huddles + facilitator boards for huddles I own
 *   - Manage: CRUD huddles the workspace admins run
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Radio, Users, PlayCircle, CheckCircle2, Sparkles, Flame, AlertTriangle, Plus, TrendingUp, Command, ListChecks, ExternalLink, BarChart3, ShieldCheck, Award, CircleUser, RefreshCw } from "lucide-react";
import AdvancedTable from "@/components/table/AdvancedTable";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import SmartRichText, { SmartRichTextDisplay } from "@/components/SmartRichText";
import HuddleRespondDialog from "@/components/HuddleRespondDialog";
import { useSearchParams } from "react-router-dom";

const DOW = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

// Iter 145 — Strip HTML to a plain-text snippet for card previews. Rich-text
// answers may contain <p>/<br>/<strong> etc. — we don't want to render markup
// inside a compact preview line.
function _snippet(v) {
  if (!v) return "";
  const s = String(v);
  if (!/<\/?[a-z][^>]*>/i.test(s)) return s;
  const el = document.createElement("div");
  el.innerHTML = s;
  return (el.textContent || el.innerText || "").trim();
}

// Iter 145 — Colour ramp used across ribbons, tiles and progress bars.
function rateColorHex(pct) {
  if (pct >= 80) return "#10b981"; // emerald-500
  if (pct >= 50) return "#f59e0b"; // amber-500
  return "#ef4444"; // red-500
}

export default function Huddles() {
  const [tab, setTab] = useState("today");
  const [openInstanceId, setOpenInstanceId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Iter 145 — deep-link support at page level: /app/huddles?instance=<id>
  // opens the instance-detail dialog directly and auto-switches to the
  // 'All instances' tab so context is obvious. Lifting this out of
  // InstancesTab is critical — inactive tabs are unmounted, so a param-
  // scoped effect inside a Tab component never fires on cold-load.
  useEffect(() => {
    const iid = searchParams.get("instance");
    if (iid) {
      setOpenInstanceId(iid);
      setTab("instances");
    }
  }, [searchParams]);

  const closeInstance = useCallback(() => {
    setOpenInstanceId(null);
    if (searchParams.get("instance")) {
      const next = new URLSearchParams(searchParams);
      next.delete("instance");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <div className="space-y-6" data-testid="huddles-page">
      <PageHeader
        breadcrumb="Rituals"
        title="Daily huddles"
        description="Multiple huddles per day across teams, projects, or roles. Async by default — AI collates, tags blockers, tracks streaks and nudges silent seats."
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none h-10">
          <TabsTrigger value="today" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="huddles-tab-today">
            <Radio className="w-4 h-4 mr-1.5" /> Today
          </TabsTrigger>
          <TabsTrigger value="instances" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="huddles-tab-instances">
            <ListChecks className="w-4 h-4 mr-1.5" /> All instances
          </TabsTrigger>
          <TabsTrigger value="manage" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="huddles-tab-manage">
            <Users className="w-4 h-4 mr-1.5" /> Manage
          </TabsTrigger>
          <TabsTrigger value="health" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="huddles-tab-health">
            <Flame className="w-4 h-4 mr-1.5" /> Team health
          </TabsTrigger>
        </TabsList>
        <TabsContent value="today" className="mt-4"><TodayTab /></TabsContent>
        <TabsContent value="instances" className="mt-4"><InstancesTab openInstanceId={openInstanceId} setOpenInstanceId={setOpenInstanceId} /></TabsContent>
        <TabsContent value="manage" className="mt-4"><ManageTab /></TabsContent>
        <TabsContent value="health" className="mt-4"><TeamHealthTab /></TabsContent>
      </Tabs>
      {/* Page-level Instance Detail Dialog — renders regardless of active tab so deep-links work. */}
      {openInstanceId && (
        <InstanceDetailDialog
          instanceId={openInstanceId}
          onClose={closeInstance}
        />
      )}
    </div>
  );
}

// ─────────────────────── Today ───────────────────────

function TodayTab() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [followups, setFollowups] = useState([]);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const [my, fu] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/my-huddles-today`),
        api.get(`/workspaces/${activeWorkspaceId}/huddle-followups`),
      ]);
      setRows(my.data || []);
      setFollowups(fu.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't load")); }
    finally { setLoading(false); }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      {followups.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 p-3" data-testid="huddle-followups">
          <div className="text-xs font-semibold uppercase tracking-widest text-amber-800 mb-1"><AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> {followups.length} unresolved blockers &gt; 48h</div>
          <ul className="text-[11px] space-y-1">
            {followups.slice(0, 5).map((f) => (
              <li key={f.id} className="flex items-center gap-2">
                <span className="flex-1">User {f.user_id?.slice(0, 8)} still blocked (response {f.response_id?.slice(0, 8)}).</span>
                <Button size="sm" variant="outline" className="rounded-none h-6 text-[10px]"
                  onClick={async () => { await api.post(`/huddle-followups/${f.id}/resolve`); toast.success("Resolved"); load(); }}
                  data-testid={`huddle-followup-resolve-${f.id}`}>Resolve</Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {loading ? <div className="text-sm text-muted-foreground py-6">Loading…</div>
        : rows.length === 0 ? <EmptyState title="No huddles for you today" description="You'll see live huddles here whenever one you're a participant of is open." icon={Radio} testid="huddles-today-empty" />
          : <ul className="space-y-2" data-testid="my-huddles-list">
            {rows.map((r) => {
              const ribbonColor = r.color || (r.my_status === "submitted" ? "#22c55e" : "hsl(var(--primary))");
              const rate = r.response_rate_pct || 0;
              const rateColor = rate >= 80 ? "text-emerald-600" : rate >= 50 ? "text-amber-600" : "text-red-500";
              return (
                <li key={r.instance_id} className="border border-border p-3 hover:bg-secondary/20 flex items-center gap-3 cursor-pointer" onClick={() => setSelected(r)} data-testid={`huddle-today-row-${r.instance_id}`}>
                  <div className="w-2 h-10 shrink-0" style={{ background: ribbonColor }} title={r.color ? "Huddle color" : ""} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.huddle_name}</div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                      <span>{(r.scheduled_at || "").slice(11, 16)}</span>
                      <span>·</span>
                      <span>You: <b className={r.my_status === "submitted" ? "text-emerald-600" : "text-foreground"}>{r.my_status}</b></span>
                      {r.participants_required > 0 && (
                        <>
                          <span>·</span>
                          <span className={rateColor} data-testid={`huddle-today-rate-${r.instance_id}`}>
                            {r.submitted_count}/{r.participants_required} in ({rate}%)
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  {r.my_status === "submitted" ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <PlayCircle className="w-4 h-4 text-primary" />}
                </li>
              );
            })}
          </ul>}
      {selected && (
        <HuddleRespondDialog inst={selected} onClose={() => setSelected(null)} onSubmitted={() => { setSelected(null); load(); }} />
      )}
    </div>
  );
}

function RespondDialog({ inst, onClose, onSubmitted }) {
  const { activeWorkspaceId } = useAuth();
  const [answers, setAnswers] = useState(inst.my_answers || {});
  const [board, setBoard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chat, setChat] = useState([]);
  const [chatBody, setChatBody] = useState("");
  const [participants, setParticipants] = useState([]);
  const [streaks, setStreaks] = useState([]);

  const loadChat = useCallback(async () => {
    try {
      const { data } = await api.get(`/huddle-instances/${inst.instance_id}/chat`);
      setChat(data || []);
    } catch { /* noop */ }
  }, [inst.instance_id]);

  // Iter 136 — pull participant roster + streak leaderboard for right-rail.
  useEffect(() => {
    if (!inst.huddle_id) return;
    api.get(`/huddles/${inst.huddle_id}/participants`).then(({ data }) => setParticipants(data || [])).catch(() => {});
    api.get(`/huddles/${inst.huddle_id}/streaks`).then(({ data }) => setStreaks(data || [])).catch(() => {});
  }, [inst.huddle_id]);

  useEffect(() => {
    api.get(`/huddle-instances/${inst.instance_id}`).then(({ data }) => setBoard(data)).catch(() => {});
    loadChat();
    // Iter 131 perf — poll every 10s, ONLY while the tab is visible.
    // Prior 4s tick was hammering the origin from every open dialog.
    let t = null;
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      loadChat();
      api.get(`/huddle-instances/${inst.instance_id}`).then(({ data }) => setBoard(data)).catch(() => {});
    };
    const onVis = () => { if (document.visibilityState === "visible") tick(); };
    t = setInterval(tick, 10000);
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [inst.instance_id, loadChat]);

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      await api.post(`/huddle-instances/${inst.instance_id}/submit`, { answers });
      toast.success("Response saved");
      onSubmitted();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  }, [answers, inst.instance_id, onSubmitted]);

  // Iter 136 — Cmd/Ctrl+Enter submits from anywhere in the dialog.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        if (!busy) submit();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [submit, busy]);

  const sendChat = async () => {
    if (!chatBody.trim()) return;
    try {
      await api.post(`/huddle-instances/${inst.instance_id}/chat`, { body: chatBody.trim() });
      setChatBody("");
      loadChat();
    } catch (e) { toast.error(apiErrorMessage(e, "Chat send failed")); }
  };

  // Streak lookup by user_id for badges on the roster.
  const streakByUser = useMemo(() => {
    const m = new Map();
    streaks.forEach((s) => m.set(s.user_id, s));
    return m;
  }, [streaks]);
  // Whoever submitted THIS instance counts as "live" (green dot).
  const submittedIds = useMemo(() => new Set(
    (board?.responses || []).filter((r) => r.status === "submitted").map((r) => r.user_id)
  ), [board]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-6xl w-[95vw] rounded-none max-h-[92vh] overflow-y-auto" data-testid="huddle-respond-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {inst.huddle_name}
            <span className="ml-2 text-[10px] font-normal text-muted-foreground inline-flex items-center gap-1 border border-border px-1.5 py-0.5">
              <Command className="w-3 h-3" /> + Enter to submit
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-12 gap-5">
          {/* Update form — bigger cards, easier scanning */}
          <div className="col-span-12 md:col-span-5 space-y-4">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Your update</div>
            {(inst.prompts || []).map((p) => (
              <div key={p.key} className="border border-border p-3 space-y-2 bg-background hover:border-primary/40 transition-colors">
                <Label className="label-eyebrow text-[11px]">{p.label}</Label>
                {p.kind === "rating" ? (
                  <div className="flex gap-1.5" data-testid={`huddle-answer-${p.key}`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" onClick={() => setAnswers((prev) => ({ ...prev, [p.key]: n }))}
                        className={`w-10 h-10 border text-sm font-semibold transition-colors ${Number(answers[p.key]) === n ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/60"}`}
                        data-testid={`huddle-rating-${p.key}-${n}`}>{n}</button>
                    ))}
                  </div>
                ) : p.kind === "long_text" ? (
                  <SmartRichText
                    value={answers[p.key] || ""}
                    onChange={(html) => setAnswers((prev) => ({ ...prev, [p.key]: html }))}
                    placeholder="Type here…"
                    minRows={3}
                    maxHeight={280}
                    workspaceId={activeWorkspaceId}
                    mediaMode="url-only"
                    testid={`huddle-answer-${p.key}`}
                  />
                ) : (
                  <Input value={answers[p.key] || ""} onChange={(e) => setAnswers((prev) => ({ ...prev, [p.key]: e.target.value }))} className="h-9 rounded-none" data-testid={`huddle-answer-${p.key}`} />
                )}
              </div>
            ))}
            <Button onClick={submit} disabled={busy} className="rounded-none w-full h-10" data-testid="huddle-submit-btn">
              {busy ? "Saving…" : "Submit update"}
            </Button>
          </div>

          {/* Live board */}
          <div className="col-span-12 md:col-span-4 border-t md:border-t-0 md:border-l border-border md:pl-4 pt-4 md:pt-0 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Board <span className="normal-case text-[10px] text-muted-foreground/70">· live · 10s</span></div>
            {!board ? <div className="text-[11px] text-muted-foreground">Loading…</div> :
              <>
                <div className="grid grid-cols-4 gap-2 text-center">
                  {Object.entries(board.ai?.totals || {}).map(([k, v]) => (
                    <div key={k} className="border border-border p-2">
                      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{k}</div>
                      <div className="text-lg font-bold">{v}</div>
                    </div>
                  ))}
                </div>
                {board.ai?.confidence_avg && (
                  <div className="text-[11px]">Avg confidence: <b>{board.ai.confidence_avg}</b> / 5</div>
                )}
                {board.ai?.ai_summary && (
                  <div className="border border-primary/30 bg-primary/5 p-2 text-[11px]">
                    <div className="text-[9px] uppercase tracking-widest text-primary mb-1"><Sparkles className="w-3 h-3 inline mr-1" /> AI</div>
                    {board.ai.ai_summary}
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">Recent responses</div>
                <ul className="space-y-1 text-[11px] max-h-40 overflow-y-auto">
                  {(board.responses || []).filter((r) => r.status === "submitted").map((r) => (
                    <li key={r.id} className="border-l-2 border-green-500 pl-2">
                      <b>{r.user_name}</b> — {_snippet(r.answers?.today || r.answers?.yesterday || "").slice(0, 80)}
                    </li>
                  ))}
                </ul>
              </>
            }
          </div>

          {/* Right rail — participants + streaks + chat */}
          <div className="col-span-12 md:col-span-3 border-t md:border-t-0 md:border-l border-border md:pl-4 pt-4 md:pt-0 flex flex-col" data-testid="huddle-chat-panel">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">Participants <span className="normal-case text-[10px]">({participants.length})</span></div>
            <ul className="border border-border max-h-40 overflow-y-auto divide-y divide-border" data-testid="huddle-participants-list">
              {participants.length === 0 && <li className="p-2 text-[10px] italic text-muted-foreground">No expected participants.</li>}
              {participants.map((p) => {
                const s = streakByUser.get(p.id);
                const live = submittedIds.has(p.id);
                return (
                  <li key={p.id} className="flex items-center gap-2 px-2 py-1 text-[11px]" data-testid={`huddle-participant-${p.id}`}>
                    <span className={`w-2 h-2 rounded-full ${live ? "bg-green-500" : "bg-muted-foreground/30"}`} title={live ? "Submitted today" : "Not submitted"} />
                    <span className="flex-1 truncate">{p.name || p.email || p.id.slice(0, 8)}</span>
                    {s?.current > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] text-amber-700 border border-amber-300 px-1" title={`Current streak · longest ${s.longest || 0}`}>
                        <Flame className="w-2.5 h-2.5" /> {s.current}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-4 mb-2">Chat</div>
            <div className="flex-1 max-h-64 overflow-y-auto space-y-1 border border-border p-2" data-testid="huddle-chat-messages">
              {chat.length === 0 && <div className="text-[11px] italic text-muted-foreground">No messages yet — say hi.</div>}
              {chat.map((m) => (
                <div key={m.id} className="text-[11px]" data-testid={`huddle-chat-msg-${m.id}`}>
                  <b>{m.user_name}</b> <span className="text-muted-foreground">{(m.created_at || "").slice(11, 16)}</span>
                  <div className="whitespace-pre-wrap break-words">{m.body}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-1 mt-2">
              <Input value={chatBody} onChange={(e) => setChatBody(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) { e.preventDefault(); sendChat(); } }} placeholder="Type a message…" className="h-8 rounded-none text-xs" data-testid="huddle-chat-input" />
              <Button onClick={sendChat} className="rounded-none h-8" data-testid="huddle-chat-send">Send</Button>
            </div>
            <p className="text-[9px] text-muted-foreground italic mt-1">Chat is archived to this huddle instance.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────── Team Health ───────────────────────

function Sparkline({ points, color = "hsl(var(--primary))", height = 24, width = 90 }) {
  // points: [{value, sample}]; nulls draw a gap.
  const vals = points.map((p) => (typeof p.value === "number" ? p.value : null));
  const numeric = vals.filter((v) => v !== null);
  if (numeric.length === 0) {
    return <svg width={width} height={height}><text x="0" y={height / 2 + 3} className="text-[9px] fill-muted-foreground">— no data —</text></svg>;
  }
  const min = Math.min(...numeric);
  const max = Math.max(...numeric);
  const span = max - min || 1;
  const stepX = width / Math.max(1, points.length - 1);
  const path = vals.map((v, i) => {
    if (v === null) return null;
    const x = i * stepX;
    const y = height - ((v - min) / span) * (height - 4) - 2;
    return `${x},${y}`;
  });
  // Split into segments where nulls break the line.
  const segments = [];
  let seg = [];
  path.forEach((p) => {
    if (p === null) { if (seg.length) segments.push(seg); seg = []; }
    else seg.push(p);
  });
  if (seg.length) segments.push(seg);
  return (
    <svg width={width} height={height} className="overflow-visible">
      {segments.map((s, i) => (
        <polyline key={i} points={s.join(" ")} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      ))}
      {vals.map((v, i) => v === null ? null : (
        <circle key={i} cx={i * stepX} cy={height - ((v - min) / span) * (height - 4) - 2} r="1.5" fill={color} />
      ))}
    </svg>
  );
}

function TeamHealthTab() {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState(null);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillUid, setDrillUid] = useState(null);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    Promise.all([
      api.get(`/workspaces/${activeWorkspaceId}/team-health`),
      api.get(`/workspaces/${activeWorkspaceId}/huddle-leaderboard?limit=10`).catch(() => ({ data: null })),
    ]).then(([h, l]) => {
      setData(h.data); setBoard(l.data); setLoading(false);
    }).catch(() => setLoading(false));
  }, [activeWorkspaceId]);
  if (loading) return <div className="text-sm text-muted-foreground py-6">Loading…</div>;
  if (!data) return <div className="text-sm text-muted-foreground py-6">No data yet.</div>;
  const cellClass = (s) => {
    if (s === "submitted") return "bg-green-500";
    if (s === "missed") return "bg-red-400";
    if (s === "pending") return "bg-amber-300";
    return "bg-secondary/40";
  };
  // Per-user 7-day sparkline: 1 for submitted else 0/null.
  const userSpark = (row) => row.days.map((s) => ({
    value: s === "submitted" ? 1 : (s === "missed" || s === "pending" ? 0 : null),
  }));
  const wsTrendPoints = (data.confidence_trend || []).map((p) => ({ value: p.avg }));
  return (
    <div className="space-y-4" data-testid="team-health-page">
      <div className="border border-border">
        <div className="px-3 py-2 bg-muted/30 border-b border-border text-xs font-semibold uppercase tracking-widest flex items-center gap-2">
          <span>7-day participation heatmap</span>
          <span className="normal-case text-[10px] font-normal text-muted-foreground tracking-normal">click a name for the 30-day drilldown</span>
        </div>
        <div className="p-3 overflow-x-auto">
          <table className="text-xs w-full">
            <thead><tr className="text-muted-foreground"><th className="text-left px-2 py-1">Member</th>{data.days.map((d) => <th key={d} className="px-1 py-1 font-mono text-[10px]">{d.slice(5)}</th>)}<th className="text-left px-2 py-1 font-normal">Trend</th></tr></thead>
            <tbody>
              {data.heatmap.length === 0 && <tr><td className="p-3 italic text-muted-foreground" colSpan={data.days.length + 2}>No huddle data in the last 7 days.</td></tr>}
              {data.heatmap.map((row) => (
                <tr key={row.user_id} className="hover:bg-secondary/20 cursor-pointer" onClick={() => setDrillUid(row.user_id)} data-testid={`team-health-row-${row.user_id}`}>
                  <td className="px-2 py-1 whitespace-nowrap font-medium">{row.name}</td>
                  {row.days.map((s, i) => (
                    <td key={i} className="px-1 py-1"><div className={`w-5 h-5 ${cellClass(s)}`} title={s || "no huddle"} /></td>
                  ))}
                  <td className="px-2 py-1"><Sparkline points={userSpark(row)} color="hsl(142 71% 40%)" width={80} height={20} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <div className="border border-border">
          <div className="px-3 py-2 bg-muted/30 border-b border-border text-xs font-semibold uppercase tracking-widest">Top-5 unresolved blockers</div>
          <ul className="p-3 text-xs space-y-1">
            {data.top_blockers.length === 0 && <li className="italic text-muted-foreground">None open.</li>}
            {data.top_blockers.map((b) => (
              <li key={b.id} className="flex items-center gap-2"><AlertTriangle className="w-3 h-3 text-amber-600" /> <span className="flex-1 truncate">{b.user_name}</span><span className="text-[10px] text-muted-foreground">{(b.created_at || "").slice(0, 10)}</span></li>
            ))}
          </ul>
        </div>
        <div className="border border-border">
          <div className="px-3 py-2 bg-muted/30 border-b border-border text-xs font-semibold uppercase tracking-widest flex items-center justify-between">
            <span>Confidence by department</span>
            <TrendingUp className="w-3 h-3 text-muted-foreground" />
          </div>
          <ul className="p-3 text-xs space-y-1">
            {data.confidence_by_department.length === 0 && <li className="italic text-muted-foreground">No confidence signals yet.</li>}
            {data.confidence_by_department.map((c) => (
              <li key={c.department_id} className="flex items-center gap-2"><span className="flex-1 truncate font-mono text-[10px]">{c.department_id.slice(0, 12)}</span><b>{c.avg ?? "—"}</b><span className="text-[10px] text-muted-foreground">/5 · n={c.sample}</span></li>
            ))}
          </ul>
          <div className="border-t border-border px-3 py-2 flex items-center gap-2" data-testid="team-health-ws-trend">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">7d trend</span>
            <Sparkline points={wsTrendPoints} width={140} height={28} />
          </div>
        </div>
        <div className="border border-border">
          <div className="px-3 py-2 bg-muted/30 border-b border-border text-xs font-semibold uppercase tracking-widest">Silent 3+ huddles</div>
          <ul className="p-3 text-xs space-y-1" data-testid="team-health-silent">
            {data.silent_users.length === 0 && <li className="italic text-muted-foreground">Nobody silent — nice.</li>}
            {data.silent_users.map((s) => (
              <li key={s.user_id} className="flex items-center gap-2 hover:bg-secondary/20 cursor-pointer" onClick={() => setDrillUid(s.user_id)}><span className="flex-1 truncate">{s.name}</span><span className="text-[10px] text-muted-foreground">{s.recent.join(" · ")}</span></li>
            ))}
          </ul>
        </div>
      </div>
      {drillUid && (
        <MemberDrilldown workspaceId={activeWorkspaceId} userId={drillUid} onClose={() => setDrillUid(null)} />
      )}
      <AttendanceLeaderboard onDrilldown={setDrillUid} />
      <StreakLeaderboard board={board} onDrilldown={setDrillUid} />
    </div>
  );
}

function StreakLeaderboard({ board, onDrilldown }) {
  if (!board) return null;
  const streaks = board.streaks || [];
  const movers = board.movers || [];
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="huddle-leaderboard">
      <div className="border border-border">
        <div className="px-3 py-2 bg-muted/30 border-b border-border text-xs font-semibold uppercase tracking-widest flex items-center gap-2">
          <Flame className="w-3.5 h-3.5 text-amber-500" />
          <span>Top-10 streaks</span>
          <span className="normal-case text-[10px] font-normal text-muted-foreground tracking-normal">consecutive-day check-ins</span>
        </div>
        <ol className="p-2 text-xs divide-y divide-border" data-testid="leaderboard-streaks">
          {streaks.length === 0 && <li className="p-3 italic text-muted-foreground">No streaks yet — the daily accountability job populates this once huddles start running.</li>}
          {streaks.map((r, i) => (
            <li key={r.user_id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-secondary/20 cursor-pointer" onClick={() => onDrilldown(r.user_id)} data-testid={`leaderboard-streak-${r.user_id}`}>
              <span className="w-5 text-center font-mono text-muted-foreground">{i + 1}</span>
              <span className="flex-1 truncate font-medium">{r.name}</span>
              <span className="inline-flex items-center gap-0.5 text-amber-700 border border-amber-300 bg-amber-50 px-1.5 py-0.5 font-semibold">
                <Flame className="w-3 h-3" /> {r.current}
              </span>
              <span className="text-[10px] text-muted-foreground w-16 text-right">best {r.longest}</span>
            </li>
          ))}
        </ol>
      </div>
      <div className="border border-border">
        <div className="px-3 py-2 bg-muted/30 border-b border-border text-xs font-semibold uppercase tracking-widest flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5 text-green-600" />
          <span>Biggest movers</span>
          <span className="normal-case text-[10px] font-normal text-muted-foreground tracking-normal">this week vs prior 7 days</span>
        </div>
        <ol className="p-2 text-xs divide-y divide-border" data-testid="leaderboard-movers">
          {movers.length === 0 && <li className="p-3 italic text-muted-foreground">No week-over-week gainers — everyone is steady.</li>}
          {movers.map((r, i) => (
            <li key={r.user_id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-secondary/20 cursor-pointer" onClick={() => onDrilldown(r.user_id)} data-testid={`leaderboard-mover-${r.user_id}`}>
              <span className="w-5 text-center font-mono text-muted-foreground">{i + 1}</span>
              <span className="flex-1 truncate font-medium">{r.name}</span>
              <span className="inline-flex items-center gap-0.5 text-green-700 border border-green-300 bg-green-50 px-1.5 py-0.5 font-semibold">
                +{r.delta}
              </span>
              <span className="text-[10px] text-muted-foreground w-16 text-right">{r.this_week} · was {r.prior_week}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function MemberDrilldown({ workspaceId, userId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/workspaces/${workspaceId}/team-health/user/${userId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(apiErrorMessage(e, "Couldn't load")));
  }, [workspaceId, userId]);
  const sparkPoints = (data?.sparkline || []).map((s) => ({ value: s.confidence }));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-none max-h-[85vh] overflow-y-auto" data-testid="team-health-drilldown">
        <DialogHeader><DialogTitle>{data?.user?.name || "Member"} · 30-day snapshot</DialogTitle></DialogHeader>
        {err && <div className="text-xs text-destructive">{err}</div>}
        {!data && !err && <div className="text-xs text-muted-foreground py-6">Loading…</div>}
        {data && (
          <div className="space-y-4 text-xs">
            <div className="grid grid-cols-4 gap-2">
              <StatCard label="Expected" value={data.participation.expected} />
              <StatCard label="Submitted" value={data.participation.submitted} accent="green" />
              <StatCard label="Missed" value={data.participation.missed} accent="red" />
              <StatCard label="Rate" value={data.participation.rate != null ? `${Math.round(data.participation.rate * 100)}%` : "—"} accent="primary" />
            </div>
            <div className="border border-border p-3">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                <span>Confidence · last 30 days</span>
                <span>Streak: <b>{data.streak.current}</b> current · {data.streak.longest} longest</span>
              </div>
              <Sparkline points={sparkPoints} width={480} height={40} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Recent responses ({data.recent_responses.length})</div>
              <ul className="border border-border divide-y divide-border max-h-64 overflow-y-auto" data-testid="drilldown-recent">
                {data.recent_responses.length === 0 && <li className="p-3 italic text-muted-foreground">No submissions in this window.</li>}
                {data.recent_responses.map((r) => (
                  <li key={r.id} className="p-2 space-y-0.5">
                    <div className="flex items-center gap-2">
                      <b>{r.huddle_name || r.huddle_id?.slice(0, 8)}</b>
                      <span className="text-[10px] text-muted-foreground">{(r.submitted_at || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                    {r.answers?.today && <div className="text-[11px]"><span className="text-muted-foreground">Today:</span> {_snippet(r.answers.today).slice(0, 140)}</div>}
                    {r.answers?.blockers && <div className="text-[11px]"><span className="text-amber-600">Blockers:</span> {_snippet(r.answers.blockers).slice(0, 140)}</div>}
                    {r.answers?.confidence && <div className="text-[11px]"><span className="text-muted-foreground">Confidence:</span> {r.answers.confidence}/5</div>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, accent }) {
  const cls = accent === "green" ? "text-green-600" : accent === "red" ? "text-red-500" : accent === "primary" ? "text-primary" : "text-foreground";
  return (
    <div className="border border-border p-2 text-center">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold ${cls}`}>{value}</div>
    </div>
  );
}

// ─────────────────────── Manage ───────────────────────

function ManageTab() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [wsMembers, setWsMembers] = useState([]);
  const [wsUsers, setWsUsers] = useState([]);
  const [wsDepartments, setWsDepartments] = useState([]);
  const [wsProjects, setWsProjects] = useState([]);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const [h, m, d, p] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/huddles`),
        api.get(`/workspaces/${activeWorkspaceId}/members`).catch(() => ({ data: [] })),
        api.get(`/workspaces/${activeWorkspaceId}/departments`).catch(() => ({ data: [] })),
        api.get(`/workspaces/${activeWorkspaceId}/projects?limit=200`).catch(() => ({ data: [] })),
      ]);
      setRows(h.data || []);
      setWsMembers(m.data || []);
      setWsUsers([]);
      setWsDepartments(d.data || []);
      setWsProjects(p.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't load")); }
    finally { setLoading(false); }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Author every huddle your team runs.  Cadence · scope · prompts · AI on/off.</p>
        <Button className="rounded-none" onClick={() => setEditing("new")} data-testid="huddle-new-btn"><Plus className="w-4 h-4 mr-1" /> New huddle</Button>
      </div>
      {loading ? <div className="text-sm text-muted-foreground py-6">Loading…</div>
        : rows.length === 0 ? <EmptyState title="No huddles yet" description="Set up your first daily standup — team, project or role scoped." icon={Radio} testid="huddles-manage-empty" />
          : <ul className="divide-y divide-border border border-border" data-testid="huddle-list">
            {rows.map((h) => (
              <li key={h.id} className="p-3 hover:bg-secondary/20 flex items-center gap-3" data-testid={`huddle-row-${h.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{h.name} {!h.enabled && <span className="text-[10px] text-muted-foreground">· paused</span>}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {h.schedule?.hour}:{String(h.schedule?.minute || 0).padStart(2, "0")} · {(h.schedule?.days_of_week || []).join(",")} · scope: {h.scope?.type}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="rounded-none" onClick={async () => {
                  const { data } = await api.post(`/huddles/${h.id}/start-now`);
                  toast.success(`Instance opened for ${data.date}`);
                }} data-testid={`huddle-start-${h.id}`}><PlayCircle className="w-3.5 h-3.5 mr-1" /> Start now</Button>
                <Button size="sm" variant="outline" className="rounded-none"
                          onClick={async () => {
                            try {
                              const { data } = await api.post(`/huddles/${h.id}/reconcile`);
                              if ((data.responses_added || 0) === 0) {
                                toast.success("Everyone in scope already has access.");
                              } else {
                                toast.success(`Access granted to ${data.responses_added} member(s) across ${data.instances_touched} instance(s).`);
                              }
                            } catch (e) { toast.error(apiErrorMessage(e)); }
                          }}
                          data-testid={`huddle-reconcile-${h.id}`}
                          title="Grant access to newly-added members for today's + future open instances">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh access
                </Button>
                <Button size="sm" variant="outline" className="rounded-none" onClick={() => setEditing(h.id)} data-testid={`huddle-edit-${h.id}`}>Edit</Button>
              </li>
            ))}
          </ul>}
      {editing !== null && (
        <HuddleEditor
          huddleId={editing === "new" ? null : editing}
          wsMembers={wsMembers}
          wsUsers={wsUsers}
          wsDepartments={wsDepartments}
          wsProjects={wsProjects}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function HuddleEditor({ huddleId, wsMembers, wsUsers, wsDepartments, wsProjects, onClose, onSaved }) {
  const { activeWorkspaceId } = useAuth();
  const [form, setForm] = useState({
    name: "", description: "",
    scope: { type: "custom", user_ids: [] },
    schedule: { days_of_week: ["mon", "tue", "wed", "thu", "fri"], hour: 9, minute: 30, tz_offset_min: 0 },
    prompts: [],
    ai_enabled: true, enabled: true, duration_minutes: 15,
    color: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const [busy, setBusy] = useState(false);
  const [userSearch, setUserSearch] = useState("");

  // Build a name-resolved directory: user_id → {name, email, role}.
  // `wsUsers` are the freshest name/email records; `wsMembers` supplies role.
  const memberById = useMemo(() => {
    const map = new Map();
    (wsMembers || []).filter((m) => {
      if (m.status === "deleted" || m.status === "removed") return false;
      // Skip synthetic/test roles that spam the list.
      if ((m.role || "").startsWith("sop_author_")) return false;
      if ((m.role || "").startsWith("lead_only_")) return false;
      // Must have SOMETHING we can show — real user record OR an email.
      return !!(m.user?.name || m.user?.email || m.name || m.email);
    }).forEach((m) => {
      map.set(m.user_id, {
        user_id: m.user_id,
        name: m.user?.name || m.name || m.email || m.user_id,
        email: m.user?.email || m.email || "",
        role: m.role || "",
      });
    });
    (wsUsers || []).forEach((u) => {
      const cur = map.get(u.id) || { user_id: u.id };
      map.set(u.id, {
        ...cur, user_id: u.id,
        name: u.name || cur.name || u.email || u.id,
        email: u.email || cur.email || "",
      });
    });
    return map;
  }, [wsMembers, wsUsers]);
  const activeUsers = useMemo(() =>
    Array.from(memberById.values()).sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [memberById]);
  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return activeUsers;
    return activeUsers.filter((u) =>
      (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  }, [activeUsers, userSearch]);

  // De-duplicated department + project lists (workspace has lots of test-created dupes).
  const departments = useMemo(() => {
    const seen = new Set();
    return (wsDepartments || []).filter((d) => {
      if (!d?.id || seen.has(d.id)) return false;
      seen.add(d.id); return true;
    }).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [wsDepartments]);
  const projects = useMemo(() =>
    (wsProjects || []).filter((p) => !p.deleted_at).sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [wsProjects]);

  useEffect(() => {
    if (!huddleId) return;
    api.get(`/huddles/${huddleId}`).then(({ data }) => setForm({
      name: data.name || "", description: data.description || "",
      scope: data.scope || { type: "custom", user_ids: [] },
      schedule: data.schedule || { days_of_week: [], hour: 9, minute: 30, tz_offset_min: 0 },
      prompts: data.prompts || [],
      ai_enabled: !!data.ai_enabled, enabled: !!data.enabled,
      duration_minutes: data.duration_minutes || 15,
      color: data.color || "",
    }));
  }, [huddleId]);

  const toggleDow = (d) => set("schedule", { ...form.schedule, days_of_week: form.schedule.days_of_week.includes(d) ? form.schedule.days_of_week.filter((x) => x !== d) : [...form.schedule.days_of_week, d] });
  const toggleUser = (uid) => set("scope", {
    ...form.scope,
    user_ids: (form.scope.user_ids || []).includes(uid)
      ? form.scope.user_ids.filter((x) => x !== uid)
      : [...(form.scope.user_ids || []), uid],
  });
  const selectedUsers = new Set(form.scope.user_ids || []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Give the huddle a name"); return; }
    if (form.scope.type === "custom" && !(form.scope.user_ids || []).length) {
      toast.error("Pick at least one user for a custom-scoped huddle"); return;
    }
    if (form.scope.type === "department" && !form.scope.department_id) {
      toast.error("Pick a department"); return;
    }
    if (form.scope.type === "project" && !form.scope.project_id) {
      toast.error("Pick a project"); return;
    }
    if (form.scope.type === "role" && !form.scope.role) {
      toast.error("Pick a role"); return;
    }
    setBusy(true);
    try {
      // Sanitize `color` — empty string fails the hex-pattern validator on
      // the backend. Convert to null so the field is cleared cleanly.
      const payload = { ...form, color: form.color ? form.color : null };
      if (huddleId) await api.patch(`/huddles/${huddleId}`, payload);
      else await api.post(`/workspaces/${activeWorkspaceId}/huddles`, payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-[95vw] rounded-none max-h-[90vh] overflow-y-auto" data-testid="huddle-editor">
        <DialogHeader><DialogTitle>{huddleId ? "Edit huddle" : "New huddle"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="label-eyebrow text-[10px]">Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Design standup"
              className="h-9 rounded-none" data-testid="huddle-name" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow text-[10px]">Scope</Label>
              <select value={form.scope.type}
                onChange={(e) => set("scope", { type: e.target.value, user_ids: e.target.value === "custom" ? (form.scope.user_ids || []) : undefined })}
                className="h-9 rounded-none w-full border border-border bg-background px-2 text-sm"
                data-testid="huddle-scope-type">
                <option value="custom">Custom user list</option>
                <option value="department">Department</option>
                <option value="project">Project</option>
                <option value="role">Role</option>
              </select>
            </div>
            <div>
              <Label className="label-eyebrow text-[10px]">Duration (min)</Label>
              <Input type="number" min={5} max={180} value={form.duration_minutes}
                onChange={(e) => set("duration_minutes", Number(e.target.value))}
                className="h-9 rounded-none" />
            </div>
            <div>
              <Label className="label-eyebrow text-[10px]">Color tag</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={form.color || "#f97316"}
                  onChange={(e) => set("color", e.target.value)}
                  className="h-9 w-14 rounded-none p-1 cursor-pointer"
                  data-testid="huddle-color-input"
                />
                <Input
                  value={form.color || ""}
                  onChange={(e) => set("color", e.target.value)}
                  placeholder="#f97316"
                  pattern="^#[0-9a-fA-F]{6}$"
                  className="h-9 rounded-none font-mono text-xs flex-1"
                />
                {form.color && (
                  <button type="button" onClick={() => set("color", "")} className="text-[10px] text-muted-foreground hover:text-foreground underline">Clear</button>
                )}
              </div>
            </div>
          </div>

          {form.scope.type === "custom" && (
            <div>
              <Label className="label-eyebrow text-[10px] flex items-center gap-2">
                Users
                <span className="normal-case text-foreground/60 tracking-normal">
                  ({selectedUsers.size} selected · {activeUsers.length} available)
                </span>
              </Label>
              <Input value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search by name or email…"
                className="h-8 rounded-none mt-1" data-testid="huddle-user-search" />
              <div className="mt-2 border border-border max-h-56 overflow-y-auto divide-y divide-border" data-testid="huddle-user-picker">
                {filteredUsers.length === 0 ? (
                  <div className="p-3 text-xs italic text-muted-foreground">No matching users.</div>
                ) : filteredUsers.map((u) => (
                  <label key={u.user_id} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-secondary/30 cursor-pointer" data-testid={`huddle-user-opt-${u.user_id}`}>
                    <input type="checkbox" checked={selectedUsers.has(u.user_id)}
                      onChange={() => toggleUser(u.user_id)} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{u.name}</div>
                      {u.email && <div className="text-[10px] text-muted-foreground truncate">{u.email}</div>}
                    </div>
                    {u.role && <span className="text-[10px] text-muted-foreground uppercase">{u.role}</span>}
                  </label>
                ))}
              </div>
              {selectedUsers.size > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {[...selectedUsers].map((uid) => {
                    const u = memberById.get(uid);
                    return (
                      <span key={uid} className="inline-flex items-center gap-1 border border-border px-1.5 py-0.5 text-[10px]" data-testid={`huddle-user-chip-${uid}`}>
                        {u?.name || uid.slice(0, 8)}
                        <button type="button" onClick={() => toggleUser(uid)} className="ml-0.5 hover:text-destructive">×</button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {form.scope.type === "department" && (
            <div>
              <Label className="label-eyebrow text-[10px]">Department</Label>
              <select value={form.scope.department_id || ""}
                onChange={(e) => set("scope", { ...form.scope, department_id: e.target.value })}
                className="h-9 rounded-none w-full border border-border bg-background px-2 text-sm"
                data-testid="huddle-department-select">
                <option value="">— Select department —</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}{d.code ? ` · ${d.code}` : ""}</option>
                ))}
              </select>
              {departments.length === 0 && (
                <p className="text-[11px] text-muted-foreground mt-1">No departments configured yet — create some in Employees → Departments.</p>
              )}
            </div>
          )}
          {form.scope.type === "project" && (
            <div>
              <Label className="label-eyebrow text-[10px]">Project</Label>
              <select value={form.scope.project_id || ""}
                onChange={(e) => set("scope", { ...form.scope, project_id: e.target.value })}
                className="h-9 rounded-none w-full border border-border bg-background px-2 text-sm"
                data-testid="huddle-project-select">
                <option value="">— Select project —</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.client_name ? ` · ${p.client_name}` : ""}</option>
                ))}
              </select>
              {projects.length === 0 && (
                <p className="text-[11px] text-muted-foreground mt-1">No projects yet — create one in Projects.</p>
              )}
            </div>
          )}
          {form.scope.type === "role" && (
            <div>
              <Label className="label-eyebrow text-[10px]">Workspace role</Label>
              <select value={form.scope.role || ""}
                onChange={(e) => set("scope", { ...form.scope, role: e.target.value })}
                className="h-9 rounded-none w-full border border-border bg-background px-2 text-sm"
                data-testid="huddle-role-select">
                <option value="">— Select role —</option>
                <option value="owner">Owner</option>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="member">Member</option>
                <option value="external">External</option>
              </select>
            </div>
          )}
          <div>
            <Label className="label-eyebrow text-[10px]">Days</Label>
            <div className="flex gap-1 mt-1">
              {DOW.map((d) => (
                <button key={d} type="button" onClick={() => toggleDow(d)}
                  className={`px-2 py-1 text-[10px] border ${form.schedule.days_of_week.includes(d) ? "border-primary bg-primary/10" : "border-border"}`}
                  data-testid={`huddle-dow-${d}`}>{d}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow text-[10px]">Start time</Label>
              <Input
                type="time"
                value={`${String(form.schedule.hour || 0).padStart(2, "0")}:${String(form.schedule.minute || 0).padStart(2, "0")}`}
                onChange={(e) => {
                  const [h, m] = (e.target.value || "09:30").split(":").map(Number);
                  set("schedule", { ...form.schedule, hour: h || 0, minute: m || 0 });
                }}
                className="h-9 rounded-none"
                data-testid="huddle-time"
              />
              <div className="text-[10px] text-muted-foreground mt-1">Local time · saved as {String(form.schedule.hour || 0).padStart(2, "0")}:{String(form.schedule.minute || 0).padStart(2, "0")}</div>
            </div>
            <div>
              <Label className="label-eyebrow text-[10px]">TZ offset (min from UTC)</Label>
              <Input type="number" value={form.schedule.tz_offset_min} onChange={(e) => set("schedule", { ...form.schedule, tz_offset_min: Number(e.target.value) })} className="h-9 rounded-none" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.ai_enabled} onChange={(e) => set("ai_enabled", e.target.checked)} /> <Sparkles className="w-3.5 h-3.5" /> Add AI summary + @mention detection + blocker flagging
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} /> Enabled (scheduler picks it up)
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="huddle-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────────── Iter 142 · All instances ───────────────────────

function InstancesTab({ openInstanceId, setOpenInstanceId }) {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [huddles, setHuddles] = useState([]);
  const [busy, setBusy] = useState(true);
  const [range, setRange] = useState("all");
  const [huddleId, setHuddleId] = useState("_all");
  const [daysBack, setDaysBack] = useState(30);
  const [analyticsHid, setAnalyticsHid] = useState(null);
  // Deep-link handling moved to page-level `Huddles()` in Iter 145 fix.

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const params = new URLSearchParams({ range, days_back: String(daysBack) });
      if (huddleId && huddleId !== "_all") params.set("huddle_id", huddleId);
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/huddle-instances/rollup?${params.toString()}`);
      setRows(data.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load instances")); }
    finally { setBusy(false); }
  }, [activeWorkspaceId, range, huddleId, daysBack]);

  useEffect(() => { load(); }, [load]);

  // Pull huddle definitions once for the "Filter by huddle" dropdown.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/huddles`)
      .then(({ data }) => setHuddles(data || []))
      .catch(() => {});
  }, [activeWorkspaceId]);

  const counts = useMemo(() => {
    const acc = { upcoming: 0, active: 0, past: 0 };
    for (const r of rows) acc[r.phase] = (acc[r.phase] || 0) + 1;
    return acc;
  }, [rows]);

  const columns = useMemo(() => [
    {
      key: "huddle_name", label: "Huddle", width: 240, sortable: true, filterType: "text",
      accessor: (r) => r.huddle_name || "",
      render: (r) => (
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-1.5 h-6 shrink-0" style={{ background: r.color || "var(--primary)" }} aria-hidden="true" />
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate">{r.huddle_name}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{r.status}</div>
          </div>
        </div>
      ),
    },
    {
      key: "phase", label: "Phase", width: 110, sortable: true, filterType: "enum",
      filterOptions: ["upcoming", "active", "past"],
      accessor: (r) => r.phase,
      render: (r) => <PhaseChip phase={r.phase} />,
    },
    {
      key: "scheduled_at", label: "Scheduled", width: 170, sortable: true, filterType: "date-range",
      accessor: (r) => r.scheduled_at || "",
      render: (r) => (
        <span className="text-xs font-mono">
          {r.scheduled_at ? new Date(r.scheduled_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
        </span>
      ),
    },
    {
      key: "participants_required", label: "Required", width: 90, sortable: true, align: "right",
      accessor: (r) => r.participants_required || 0,
      render: (r) => <span className="chip text-[10px]">{r.participants_required}</span>,
    },
    {
      key: "submitted", label: "Submitted", width: 90, sortable: true, align: "right", sum: true,
      accessor: (r) => r.submitted || 0,
      render: (r) => <span className="chip text-[10px] chip-approved">{r.submitted}</span>,
    },
    {
      key: "pending", label: "Pending", width: 90, sortable: true, align: "right", sum: true,
      accessor: (r) => r.pending || 0,
      render: (r) => <span className={`chip text-[10px] ${r.pending > 0 ? "chip-warning" : ""}`}>{r.pending}</span>,
    },
    {
      key: "missed", label: "Missed", width: 80, sortable: true, align: "right", sum: true,
      accessor: (r) => r.missed || 0,
      render: (r) => <span className={`chip text-[10px] ${r.missed > 0 ? "chip-rejected" : ""}`}>{r.missed}</span>,
    },
    {
      key: "response_rate_pct", label: "Response %", width: 110, sortable: true, align: "right",
      accessor: (r) => r.response_rate_pct || 0,
      render: (r) => (
        <div className="flex items-center gap-1 justify-end">
          <div className="w-14 h-1.5 bg-border relative overflow-hidden">
            <div className="absolute inset-y-0 left-0" style={{ width: `${r.response_rate_pct}%`, background: r.response_rate_pct >= 80 ? "var(--primary)" : r.response_rate_pct >= 50 ? "#f59e0b" : "#ef4444" }} />
          </div>
          <span className="text-[10px] font-mono w-8 text-right">{r.response_rate_pct}%</span>
        </div>
      ),
    },
    {
      key: "my_status", label: "My status", width: 110, sortable: true, filterType: "enum",
      filterOptions: ["submitted", "pending", "missed", "not_invited"],
      accessor: (r) => r.my_status,
      render: (r) => <MyStatusChip s={r.my_status} />,
    },
  ], []);

  return (
    <div className="space-y-4" data-testid="huddles-instances-tab">
      {/* Phase filter tiles */}
      <div className="grid grid-cols-4 gap-2">
        <PhaseTile label="All" count={rows.length} active={range === "all"} onClick={() => setRange("all")} testid="huddles-phase-all" />
        <PhaseTile label="Upcoming" count={counts.upcoming || 0} active={range === "upcoming"} onClick={() => setRange("upcoming")} color="text-blue-600" testid="huddles-phase-upcoming" />
        <PhaseTile label="Active" count={counts.active || 0} active={range === "active"} onClick={() => setRange("active")} color="text-emerald-600" testid="huddles-phase-active" />
        <PhaseTile label="Past" count={counts.past || 0} active={range === "past"} onClick={() => setRange("past")} color="text-muted-foreground" testid="huddles-phase-past" />
      </div>

      {/* Filter bar */}
      <div className="border border-border bg-muted/40 p-2 flex flex-wrap items-center gap-2">
        <span className="label-eyebrow text-[10px] ml-2">Filter</span>
        <Select value={huddleId} onValueChange={setHuddleId}>
          <SelectTrigger className="h-8 w-56 rounded-none text-xs" data-testid="huddles-filter-huddle"><SelectValue placeholder="All huddles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All huddles</SelectItem>
            {huddles.map((h) => (
              <SelectItem key={h.id} value={h.id}>
                {h.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(daysBack)} onValueChange={(v) => setDaysBack(Number(v))}>
          <SelectTrigger className="h-8 w-36 rounded-none text-xs" data-testid="huddles-filter-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" className="rounded-none h-8 text-xs ml-auto" onClick={load} disabled={busy} data-testid="huddles-reload">
          {busy ? "Loading…" : "Refresh"}
        </Button>
      </div>

      <AdvancedTable
        tableKey="huddle-instances"
        title="Huddle instances"
        rows={rows}
        columns={columns}
        loading={busy && rows.length === 0}
        emptyIcon={Radio}
        emptyTitle="No instances"
        emptyDescription={range === "upcoming" ? "No upcoming huddles scheduled in this range." : "Nothing matches your filters."}
        onRowClick={(r) => setOpenInstanceId(r.id)}
        renderRowActions={(r) => (
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="rounded-none h-7 px-2"
                    onClick={(e) => { e.stopPropagation(); setAnalyticsHid(r.huddle_id); }}
                    data-testid={`huddles-analytics-${r.id}`}
                    title="Attendance analytics for this huddle">
              <BarChart3 className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="rounded-none h-7 px-2"
                    onClick={(e) => { e.stopPropagation(); setOpenInstanceId(r.id); }}
                    data-testid={`huddles-open-${r.id}`}
                    title="Open instance detail">
              <ExternalLink className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      />
      {analyticsHid && (
        <AttendanceAnalyticsDialog
          huddleId={analyticsHid}
          onClose={() => setAnalyticsHid(null)}
        />
      )}
    </div>
  );
}

function PhaseChip({ phase }) {
  const cfg = {
    upcoming: { c: "text-blue-600 border-blue-500/40", label: "Upcoming" },
    active:   { c: "text-emerald-600 border-emerald-500/40 bg-emerald-500/5", label: "Active" },
    past:     { c: "text-muted-foreground border-border", label: "Past" },
  }[phase] || { c: "", label: phase };
  return <span className={`chip text-[10px] border ${cfg.c}`}>{cfg.label}</span>;
}

function MyStatusChip({ s }) {
  const cfg = {
    submitted: { c: "chip-approved", label: "Submitted" },
    pending:   { c: "chip-warning", label: "Pending" },
    missed:    { c: "chip-rejected", label: "Missed" },
    not_invited: { c: "", label: "—" },
  }[s] || { c: "", label: s };
  return <span className={`chip text-[10px] ${cfg.c}`}>{cfg.label}</span>;
}

function PhaseTile({ label, count, active, onClick, color = "", testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border p-3 text-left transition-colors ${
        active ? "border-primary bg-primary/10" : "border-border hover:border-foreground/50"
      }`}
      data-testid={testid}
    >
      <div className={`label-eyebrow text-[10px] ${color}`}>{label}</div>
      <div className="text-2xl font-bold mt-1 font-mono" style={{ fontFamily: "var(--font-heading)" }}>{count}</div>
    </button>
  );
}


// ─────────────────────── Iter 145 · Instance Detail Dialog ───────────────────────

function InstanceDetailDialog({ instanceId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    setBusy(true);
    api.get(`/huddle-instances/${instanceId}`)
      .then(({ data }) => { setData(data); setBusy(false); })
      .catch((e) => { setErr(apiErrorMessage(e, "Couldn't load instance")); setBusy(false); });
  }, [instanceId]);

  const inst = data?.instance || {};
  const resps = data?.responses || [];
  const ai = data?.ai || null;
  const prompts = inst.prompts || [];

  const totals = useMemo(() => {
    const acc = { submitted: 0, pending: 0, missed: 0 };
    resps.forEach((r) => { acc[r.status] = (acc[r.status] || 0) + 1; });
    acc.total = resps.length;
    acc.rate = acc.total ? Math.round(acc.submitted / acc.total * 100) : 0;
    return acc;
  }, [resps]);

  const promptCols = useMemo(() => prompts.filter((p) => p.kind !== "rating"), [prompts]);
  const ratingPrompts = useMemo(() => prompts.filter((p) => p.kind === "rating"), [prompts]);

  const csvExport = useCallback(() => {
    const cols = ["User", "Status", "Submitted at", ...prompts.map((p) => p.label || p.key)];
    const lines = [cols.join(",")];
    for (const r of resps) {
      const cells = [
        `"${(r.user_name || r.user_id || "").replace(/"/g, '""')}"`,
        r.status,
        `"${(r.submitted_at || "").replace(/"/g, '""')}"`,
        ...prompts.map((p) => {
          const v = (r.answers || {})[p.key];
          return `"${_snippet(v).replace(/"/g, '""')}"`;
        }),
      ];
      lines.push(cells.join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(inst.huddle_name || "huddle").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${inst.date || "instance"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [inst, prompts, resps]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-6xl w-[95vw] rounded-none max-h-[92vh] overflow-y-auto" data-testid="huddle-instance-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span>{inst.huddle_name || "Huddle instance"}</span>
            <span className="text-[10px] font-normal text-muted-foreground border border-border px-1.5 py-0.5">
              {inst.date} · {(inst.scheduled_at || "").slice(11, 16)} · {inst.status}
            </span>
            {inst.closed_at && (
              <span className="text-[10px] font-normal text-muted-foreground">closed {inst.closed_at.slice(0, 16).replace("T", " ")}</span>
            )}
          </DialogTitle>
        </DialogHeader>

        {err && <div className="text-xs text-destructive">{err}</div>}
        {busy && !data && <div className="text-sm text-muted-foreground py-6">Loading instance…</div>}

        {data && (
          <div className="space-y-4">
            {/* Response summary tiles */}
            <div className="grid grid-cols-5 gap-2">
              <StatCard label="Participants" value={totals.total} />
              <StatCard label="Submitted" value={totals.submitted} accent="green" />
              <StatCard label="Pending" value={totals.pending} accent="primary" />
              <StatCard label="Missed" value={totals.missed} accent="red" />
              <StatCard label="Response %" value={`${totals.rate}%`} accent={totals.rate >= 80 ? "green" : totals.rate >= 50 ? "primary" : "red"} />
            </div>

            {ai?.ai_summary && (
              <div className="border border-primary/30 bg-primary/5 p-3 text-xs" data-testid="huddle-instance-ai">
                <div className="text-[10px] uppercase tracking-widest text-primary mb-1">
                  <Sparkles className="w-3 h-3 inline mr-1" /> AI summary
                </div>
                <SmartRichTextDisplay html={ai.ai_summary} testid="huddle-instance-ai-body" />
                {Array.isArray(ai.ai_flags) && ai.ai_flags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {ai.ai_flags.map((f, i) => (
                      <span key={i} className="chip text-[10px] chip-warning">{f}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tabular listing of every participant's updates */}
            <div className="border border-border">
              <div className="px-3 py-2 bg-muted/30 border-b border-border flex items-center gap-2 text-xs">
                <span className="font-semibold uppercase tracking-widest">Everyone&apos;s updates</span>
                <span className="normal-case text-[10px] text-muted-foreground">{resps.length} participants</span>
                <div className="ml-auto flex items-center gap-1">
                  <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-xs" onClick={csvExport} data-testid="huddle-instance-csv">
                    Download CSV
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="text-xs w-full" data-testid="huddle-instance-responses-table">
                  <thead className="bg-background border-b border-border">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-medium">Member</th>
                      <th className="text-left px-2 py-1.5 font-medium">Status</th>
                      {ratingPrompts.map((p) => (
                        <th key={p.key} className="text-left px-2 py-1.5 font-medium">{p.label}</th>
                      ))}
                      {promptCols.map((p) => (
                        <th key={p.key} className="text-left px-2 py-1.5 font-medium min-w-[180px]">{p.label}</th>
                      ))}
                      <th className="text-left px-2 py-1.5 font-medium">Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resps.length === 0 && (
                      <tr><td colSpan={4 + prompts.length} className="p-3 italic text-muted-foreground text-center">
                        No participants on this instance yet.
                      </td></tr>
                    )}
                    {resps.map((r) => (
                      <tr key={r.id} className="border-t border-border/60 hover:bg-secondary/20 align-top" data-testid={`huddle-instance-response-${r.id}`}>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <CircleUser className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                            <div>
                              <div className="font-medium">{r.user_name || r.user_id?.slice(0, 8)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <ResponseStatusChip s={r.status} />
                        </td>
                        {ratingPrompts.map((p) => {
                          const v = (r.answers || {})[p.key];
                          return (
                            <td key={p.key} className="px-2 py-2 whitespace-nowrap">
                              {typeof v === "number" || (typeof v === "string" && v) ? (
                                <span className="chip text-[10px]">{v}/5</span>
                              ) : <span className="text-muted-foreground/60">—</span>}
                            </td>
                          );
                        })}
                        {promptCols.map((p) => {
                          const v = (r.answers || {})[p.key];
                          if (!v) return <td key={p.key} className="px-2 py-2 text-muted-foreground/60">—</td>;
                          const isHtml = /<\/?[a-z][^>]*>/i.test(String(v));
                          return (
                            <td key={p.key} className="px-2 py-2">
                              {isHtml ? (
                                <div className="max-w-[320px]">
                                  <SmartRichTextDisplay html={String(v)} className="text-[11px]" />
                                </div>
                              ) : (
                                <div className="max-w-[320px] whitespace-pre-wrap break-words text-[11px]">{String(v)}</div>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-2 py-2 whitespace-nowrap text-[10px] text-muted-foreground font-mono">
                          {r.submitted_at ? r.submitted_at.slice(0, 16).replace("T", " ") : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResponseStatusChip({ s }) {
  const cfg = {
    submitted: { c: "chip-approved", label: "Submitted", Icon: CheckCircle2 },
    pending: { c: "chip-warning", label: "Pending", Icon: PlayCircle },
    missed: { c: "chip-rejected", label: "Missed", Icon: AlertTriangle },
  }[s] || { c: "", label: s || "—", Icon: null };
  const IconEl = cfg.Icon;
  return (
    <span className={`chip text-[10px] inline-flex items-center gap-1 ${cfg.c}`}>
      {IconEl && <IconEl className="w-3 h-3" />} {cfg.label}
    </span>
  );
}

// ─────────────────────── Iter 145 · Attendance Analytics ───────────────────────

function AttendanceAnalyticsDialog({ huddleId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(90);

  useEffect(() => {
    setData(null);
    api.get(`/huddles/${huddleId}/attendance-analytics?days_back=${days}`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(apiErrorMessage(e, "Couldn't load analytics")));
  }, [huddleId, days]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl w-[92vw] rounded-none max-h-[90vh] overflow-y-auto" data-testid="huddle-analytics-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            {data?.huddle_name || "Attendance analytics"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-2 mb-3">
          <Label className="label-eyebrow text-[10px]">Window</Label>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="h-8 w-32 rounded-none text-xs" data-testid="huddle-analytics-days">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 6 months</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {err && <div className="text-xs text-destructive">{err}</div>}
        {!data && !err && <div className="text-sm text-muted-foreground py-6">Loading…</div>}
        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-5 gap-2">
              <StatCard label="Instances" value={data.totals?.instances || 0} />
              <StatCard label="Required" value={data.totals?.required_seats || 0} />
              <StatCard label="Submitted" value={data.totals?.submitted || 0} accent="green" />
              <StatCard label="Missed" value={data.totals?.missed || 0} accent="red" />
              <StatCard label="Avg resp %" value={`${data.totals?.avg_response_pct || 0}%`} accent="primary" />
            </div>

            {/* Trend visualization — per-instance response % bars. */}
            <div className="border border-border">
              <div className="px-3 py-2 bg-muted/30 border-b border-border text-xs font-semibold uppercase tracking-widest">
                Response % trend
              </div>
              <div className="p-3">
                {(data.series || []).length === 0 ? (
                  <div className="text-xs italic text-muted-foreground py-4 text-center">No instances in this window.</div>
                ) : (
                  <div className="flex items-end gap-0.5 h-32" data-testid="huddle-analytics-trend">
                    {data.series.map((p) => {
                      const h = Math.max(2, (p.response_rate_pct || 0) * 1.2);
                      return (
                        <div key={p.instance_id} className="flex-1 flex flex-col items-center min-w-[6px]" title={`${p.date} — ${p.response_rate_pct}% (${p.submitted}/${p.required})`}>
                          <div className="w-full transition-all" style={{ height: `${h}px`, background: rateColorHex(p.response_rate_pct) }} />
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between text-[9px] font-mono text-muted-foreground">
                  <span>{data.series[0]?.date || ""}</span>
                  <span>{data.series[data.series.length - 1]?.date || ""}</span>
                </div>
              </div>
            </div>

            {/* Recent instances tabular */}
            <div className="border border-border">
              <div className="px-3 py-2 bg-muted/30 border-b border-border text-xs font-semibold uppercase tracking-widest">
                Recent instances
              </div>
              <div className="max-h-64 overflow-y-auto">
                <table className="text-xs w-full">
                  <thead className="sticky top-0 bg-background border-b border-border">
                    <tr className="text-muted-foreground">
                      <th className="text-left px-2 py-1">Date</th>
                      <th className="text-right px-2 py-1">Required</th>
                      <th className="text-right px-2 py-1">Submitted</th>
                      <th className="text-right px-2 py-1">Missed</th>
                      <th className="text-right px-2 py-1">Response %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...(data.series || [])].reverse().slice(0, 30).map((p) => (
                      <tr key={p.instance_id} className="border-t border-border/60">
                        <td className="px-2 py-1 font-mono">{p.date}</td>
                        <td className="px-2 py-1 text-right">{p.required}</td>
                        <td className="px-2 py-1 text-right text-emerald-600">{p.submitted}</td>
                        <td className="px-2 py-1 text-right text-red-500">{p.missed}</td>
                        <td className="px-2 py-1 text-right font-semibold" style={{ color: rateColorHex(p.response_rate_pct) }}>
                          {p.response_rate_pct}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────── Iter 145 · Attendance Leaderboard ───────────────────────

function AttendanceLeaderboard({ onDrilldown }) {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(60);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    setData(null);
    api.get(`/workspaces/${activeWorkspaceId}/huddle-attendance-leaderboard?days_back=${days}&limit=5`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(apiErrorMessage(e, "Couldn't load attendance leaderboard")));
  }, [activeWorkspaceId, days]);

  const sendPreview = async () => {
    setPreviewing(true);
    try {
      const { data: res } = await api.post(`/workspaces/${activeWorkspaceId}/huddle-attendance-weekly-digest/trigger`);
      if (res?.sent) toast.success(`Preview digest sent to ${res.to}`);
      else toast.info(res?.note || "No digest to send yet");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Preview failed"));
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="border border-border" data-testid="attendance-leaderboard">
      <div className="px-3 py-2 bg-muted/30 border-b border-border flex items-center gap-2 text-xs">
        <ShieldCheck className="w-3.5 h-3.5 text-primary" />
        <span className="font-semibold uppercase tracking-widest">Attendance ranking</span>
        <span className="normal-case text-[10px] font-normal text-muted-foreground">min 3 expected huddles</span>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="ghost" className="rounded-none h-7 px-2 text-[11px]"
                  onClick={sendPreview} disabled={previewing}
                  title="Send yourself a preview of the Monday email digest"
                  data-testid="attendance-preview-digest">
            {previewing ? "Sending…" : "Email preview"}
          </Button>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="h-7 w-32 rounded-none text-[11px]" data-testid="attendance-leaderboard-days">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 6 months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {err && <div className="p-3 text-xs text-destructive">{err}</div>}
      {!data && !err && <div className="p-3 text-xs text-muted-foreground">Loading…</div>}
      {data && (
        <div className="grid md:grid-cols-2 divide-x divide-border">
          <LeaderboardColumn
            title="Top 5 most reliable"
            icon={Award}
            iconColor="text-emerald-600"
            rows={data.most_reliable || []}
            testidPrefix="attendance-reliable"
            onDrilldown={onDrilldown}
            emptyMsg="Not enough huddle history yet."
          />
          <LeaderboardColumn
            title="Top 5 most missed"
            icon={AlertTriangle}
            iconColor="text-red-500"
            rows={data.most_missed || []}
            testidPrefix="attendance-missed"
            onDrilldown={onDrilldown}
            emptyMsg="Nobody missing yet — nice."
          />
        </div>
      )}
    </div>
  );
}

function LeaderboardColumn({ title, icon: IconEl, iconColor, rows, testidPrefix, onDrilldown, emptyMsg }) {
  return (
    <div>
      <div className="px-3 py-2 text-xs font-semibold uppercase tracking-widest flex items-center gap-2 border-b border-border">
        <IconEl className={`w-3.5 h-3.5 ${iconColor}`} />
        <span>{title}</span>
      </div>
      <ol className="p-2 text-xs divide-y divide-border" data-testid={testidPrefix}>
        {rows.length === 0 && <li className="p-3 italic text-muted-foreground">{emptyMsg}</li>}
        {rows.map((r, i) => (
          <li key={r.user_id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-secondary/20 cursor-pointer" onClick={() => onDrilldown?.(r.user_id)} data-testid={`${testidPrefix}-${r.user_id}`}>
            <span className="w-5 text-center font-mono text-muted-foreground">{i + 1}</span>
            <span className="flex-1 truncate font-medium">{r.name}</span>
            <span className="font-semibold" style={{ color: rateColorHex(r.attendance_pct) }}>
              {r.attendance_pct}%
            </span>
            <span className="text-[10px] text-muted-foreground w-24 text-right">{r.submitted}/{r.expected} · miss {r.missed}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}